/**
 * Nowa karta produktu.
 *
 * Zasada nadrzędna: nie zastępujemy mechaniki WooCommerce. Kafelki i pigułki
 * są zwykłymi polami radio, które ustawiają wartość natywnego <select>
 * i wyzwalają `change`. Za dopasowanie wariantu, cenę, stany magazynowe
 * i stan przycisku nadal odpowiada skrypt wc-add-to-cart-variation.
 */

import Swiper from 'swiper/bundle';

const SELECTED_CLASS = 'is-active';

/**
 * Karuzela polecanych produktów (decyzja projektowa: przewijana, nie siatka).
 */
function initCrossCarousel(root) {
    const container = root.querySelector('.pdp-cross__carousel');

    if (!container) {
        return;
    }

    // eslint-disable-next-line no-new
    new Swiper(container, {
        slidesPerView: 2,
        spaceBetween: 13,
        watchOverflow: true,
        scrollbar: {
            el: container.querySelector('.swiper-scrollbar'),
            draggable: true,
        },
        breakpoints: {
            992: {
                slidesPerView: 4,
                spaceBetween: 13,
            },
        },
    });
}

/**
 * Natywne pole wyboru odpowiadające atrybutowi.
 */
function nativeSelect(form, attribute) {
    return form.querySelector(`select[name="attribute_${attribute}"]`);
}

/**
 * Ustawia wariant i informuje o tym WooCommerce.
 */
function applyAttribute(form, attribute, value) {
    const select = nativeSelect(form, attribute);

    if (!select || select.value === value) {
        return;
    }

    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * Wybór gramatury i pozostałych atrybutów w trybie ogólnym.
 */
function initAttributeRadios(form) {
    const radios = form.querySelectorAll('input[type="radio"][data-pdp-attribute]');

    radios.forEach((radio) => {
        radio.addEventListener('change', () => {
            if (!radio.checked) {
                return;
            }

            const attribute = radio.dataset.pdpAttribute;
            const value = radio.dataset.pdpValue || radio.value;

            applyAttribute(form, attribute, value);
        });
    });

    // Stan początkowy — bez tego wariant nie byłby wybrany po wejściu na stronę.
    radios.forEach((radio) => {
        if (radio.checked) {
            applyAttribute(form, radio.dataset.pdpAttribute, radio.dataset.pdpValue || radio.value);
        }
    });
}

/**
 * Dwustopniowy wybór mielenia: Ziarna / Mielona → metoda.
 */
function initGrind(form) {
    const methodsBox = form.querySelector('[data-pdp-grind-methods]');
    const typeRadios = form.querySelectorAll('input[name="pdp_mielenie_typ"]');

    if (!typeRadios.length) {
        return;
    }

    const checkedMethod = () => form.querySelector('input[name="pdp_mielenie_metoda"]:checked');

    // Rozwinieciem panelu metod steruje klasa is-open (CSS animuje
    // grid-template-rows); atrybut hidden schodzi raz - trzymal panel
    // przed inicjalizacja skryptu.
    if (methodsBox) {
        methodsBox.hidden = false;
    }

    const update = () => {
        const selected = form.querySelector('input[name="pdp_mielenie_typ"]:checked');

        if (!selected) {
            return;
        }

        const isGround = selected.value === 'mielona';

        if (methodsBox) {
            methodsBox.classList.toggle('is-open', isGround);
        }

        if (isGround) {
            const method = checkedMethod();

            if (method) {
                applyAttribute(form, method.dataset.pdpAttribute, method.value);
            }
        } else {
            applyAttribute(form, selected.dataset.pdpAttribute, selected.dataset.pdpValue);
        }
    };

    typeRadios.forEach((radio) => radio.addEventListener('change', update));
    update();
}

/**
 * Stany niedostępności wariantów.
 *
 * Wyszarza wyłącznie opcje, które nie prowadzą do żadnego dostępnego
 * wariantu (kombinacje liczone z data-product_variations), oraz przycisk
 * dodania, gdy bieżący wybór jest niedostępny. Tooltip przez atrybut title
 * na etykiecie — dlatego pola dostają disabled, ale kursor i podpowiedź
 * obsługuje otaczający label.
 */
function initAvailability(root, form) {
    if (typeof window.jQuery === 'undefined') {
        return;
    }

    const $form = window.jQuery(form);
    const TITLE = 'Brak w magazynie';

    const variations = () => {
        const data = $form.data('product_variations');
        return Array.isArray(data) ? data : [];
    };

    // Wariant pasuje, gdy kazdy zadany atrybut zgadza sie z jego wartoscia
    // albo wariant ma wartosc pusta ("dowolna").
    const matches = (variation, attrs) => Object.entries(attrs).every(([tax, value]) => {
        const own = variation.attributes['attribute_' + tax];
        return own === '' || own === value;
    });

    const anyInStock = (attrs) => variations().some((v) => v.is_in_stock && matches(v, attrs));

    const setOptionState = (input, available) => {
        const wrap = input.closest('label');
        input.disabled = !available;

        if (wrap) {
            wrap.classList.toggle('is-oos', !available);

            if (available) {
                wrap.removeAttribute('title');
            } else {
                wrap.setAttribute('title', TITLE);
            }
        }
    };

    const selectedWeight = () => {
        const radio = form.querySelector('input[name="pdp_waga"]:checked');
        return radio ? radio.value : null;
    };

    const updateOptions = () => {
        if (!variations().length) {
            return;
        }

        form.querySelectorAll('input[name="pdp_waga"]').forEach((radio) => {
            setOptionState(radio, anyInStock({ pa_waga: radio.value }));
        });

        const base = {};
        const weight = selectedWeight();

        if (weight) {
            base.pa_waga = weight;
        }

        const methodValues = [...form.querySelectorAll('input[name="pdp_mielenie_metoda"]')].map((r) => r.value);

        form.querySelectorAll('input[name="pdp_mielenie_typ"]').forEach((radio) => {
            const available = radio.value === 'mielona'
                ? methodValues.some((m) => anyInStock({ ...base, 'pa_czy-kawa-ma-zostac-zmielona': m }))
                : anyInStock({ ...base, 'pa_czy-kawa-ma-zostac-zmielona': radio.dataset.pdpValue || radio.value });
            setOptionState(radio, available);
        });

        form.querySelectorAll('input[name="pdp_mielenie_metoda"]').forEach((radio) => {
            setOptionState(radio, anyInStock({ ...base, 'pa_czy-kawa-ma-zostac-zmielona': radio.value }));
        });

        // Tryb ogolny (rozmiar, kolor, wartosc...)
        form.querySelectorAll('input[data-pdp-attribute]:not([name="pdp_waga"]):not([name^="pdp_mielenie"])').forEach((radio) => {
            setOptionState(radio, anyInStock({ [radio.dataset.pdpAttribute]: radio.dataset.pdpValue || radio.value }));
        });
    };

    const buttons = [root.querySelector('.pdp-cart__button'), root.querySelector('[data-pdp-sticky-submit]')];

    const setCta = (oos) => {
        buttons.forEach((button) => {
            if (!button) {
                return;
            }

            button.classList.toggle('is-oos', oos);

            if (oos) {
                button.setAttribute('title', TITLE);
                button.setAttribute('aria-disabled', 'true');
            } else {
                button.removeAttribute('title');
                button.removeAttribute('aria-disabled');
            }
        });
    };

    buttons.forEach((button) => {
        if (button) {
            button.addEventListener('click', (event) => {
                if (button.classList.contains('is-oos')) {
                    event.preventDefault();
                    event.stopImmediatePropagation();
                }
            }, true);
        }
    });

    $form.on('show_variation', (event, variation) => setCta(!variation || !variation.is_in_stock));
    $form.on('hide_variation', () => setCta(!anyInStock({})));
    form.addEventListener('change', updateOptions);

    // Stan poczatkowy (po inicjalizacji WC dane wariantow juz sa).
    updateOptions();
    setCta(!anyInStock({}));
}

/**
 * Przyciski plus/minus przy polu ilości.
 *
 * Delegowane na dokument, bo pole ilości występuje w formularzu produktu
 * i może być przerenderowane. Zmiana wartości wyzwala `change`, żeby
 * WooCommerce (i ewentualne wtyczki) widziały nową ilość.
 */
function initQuantity(root) {
    root.addEventListener('click', (event) => {
        const button = event.target.closest('.quantity .plus, .quantity .minus');

        if (!button) {
            return;
        }

        const wrap = button.closest('.quantity');
        const input = wrap && wrap.querySelector('input.qty');

        if (!input) {
            return;
        }

        const step = parseFloat(input.step) || 1;
        const min = parseFloat(input.min);
        const max = parseFloat(input.max);
        const current = parseFloat(input.value) || 0;

        let next = button.classList.contains('plus') ? current + step : current - step;

        if (!Number.isNaN(min)) {
            next = Math.max(next, min);
        } else {
            next = Math.max(next, 1);
        }

        if (!Number.isNaN(max) && max > 0) {
            next = Math.min(next, max);
        }

        if (next !== current) {
            input.value = next;
            input.dispatchEvent(new Event('change', { bubbles: true }));
        }
    });
}

/**
 * Przełączanie zdjęć w galerii.
 */
function initGallery(root) {
    const thumbs = root.querySelectorAll('[data-photo-target]');
    const photos = root.querySelectorAll('[data-photo-index]');

    if (!thumbs.length) {
        return;
    }

    thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
            const target = thumb.dataset.photoTarget;

            photos.forEach((photo) => {
                photo.classList.toggle(SELECTED_CLASS, photo.dataset.photoIndex === target);
            });

            thumbs.forEach((other) => {
                const active = other === thumb;
                other.classList.toggle(SELECTED_CLASS, active);
                other.setAttribute('aria-selected', active ? 'true' : 'false');
            });
        });
    });
}

/**
 * Zmiana podglądu przy wyborze wariantu.
 *
 * Warianty mogą mieć własne zdjęcia (natywna funkcja WooCommerce —
 * np. kolory ekspresów). Galeria karty składa się ze slotów ACF, więc
 * zamiast podmieniać źródła obrazka AKTYWUJEMY slot pokazujący ten sam
 * plik co zdjęcie wybranego wariantu (porównanie po nazwie pliku, bez
 * sufiksów rozmiaru -600x600/-scaled). Wariant bez zdjęcia albo bez
 * dopasowania w galerii = galeria zostaje jak jest (kawy nietknięte).
 */
function initVariationPhoto(root, form) {
    if (typeof window.jQuery === 'undefined') {
        return;
    }

    const thumbs = [...root.querySelectorAll('[data-photo-target]')];

    if (!thumbs.length) {
        return;
    }

    const bazowa = (url) => {
        const plik = decodeURIComponent((url || '').split('/').pop().split('?')[0]);
        return plik.replace(/(-\d+x\d+|-scaled)(?=\.\w+$)/, '').replace(/\.\w+$/, '');
    };

    const sloty = [...root.querySelectorAll('[data-photo-index]')].map((pic) => ({
        index: pic.dataset.photoIndex,
        pliki: [...pic.querySelectorAll('source, img')]
            .map((el) => bazowa(el.getAttribute('srcset') || el.getAttribute('src')))
            .filter(Boolean),
    }));

    window.jQuery(form).on('found_variation', (event, variation) => {
        const obraz = variation && variation.image;
        const cel = bazowa(obraz && (obraz.full_src || obraz.src || obraz.url));

        if (!cel) {
            return;
        }

        const slot = sloty.find((s) => s.pliki.some((p) => p === cel));

        if (slot) {
            const thumb = thumbs.find((t) => t.dataset.photoTarget === slot.index);

            if (thumb && !thumb.classList.contains(SELECTED_CLASS)) {
                thumb.click();
            }
        }
    });
}

/**
 * Zakładki przepisu parzenia.
 */
function initBrewTabs(root) {
    const box = root.querySelector('[data-pdp-brew]');

    if (!box) {
        return;
    }

    const tabs = box.querySelectorAll('[data-pdp-brew-tab]');
    const panels = box.querySelectorAll('[data-pdp-brew-panel]');

    tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
            const target = tab.dataset.pdpBrewTab;

            tabs.forEach((other) => {
                const active = other === tab;
                other.classList.toggle(SELECTED_CLASS, active);
                other.setAttribute('aria-selected', active ? 'true' : 'false');
            });

            panels.forEach((panel) => {
                const active = panel.dataset.pdpBrewPanel === target;
                panel.classList.toggle(SELECTED_CLASS, active);
                panel.hidden = !active;
            });
        });
    });
}

/**
 * Belka zakupowa pojawiająca się po minięciu głównego przycisku.
 */
function initStickyBar(root) {
    const bar = root.querySelector('[data-pdp-sticky]');
    const cta = root.querySelector('.pdp-cart__button');

    if (!bar || !cta) {
        return;
    }

    // Pomiar pozycji zamiast IntersectionObservera: motyw przewija strone
    // wewnatrz <body>, a skokowa zmiana scrolla (kotwice, programowe scrollTo)
    // przenosi CTA z "pod viewportem" do "nad viewportem" bez zmiany stanu
    // przeciecia - IO nie emituje wtedy zadnego zdarzenia. Nasluch w fazie
    // capture lapie takze scroll kontenerow wewnetrznych.
    let zaplanowane = false;

    // Widocznoscia steruje klasa (CSS animuje transform), atrybut hidden
    // schodzi raz - trzymal belke przed inicjalizacja skryptu.
    bar.hidden = false;

    const update = () => {
        zaplanowane = false;
        // Belka pokazuje sie dopiero po przewinieciu PONIZEJ przycisku.
        bar.classList.toggle('is-visible', cta.getBoundingClientRect().bottom < 0);
    };

    const onScroll = () => {
        if (!zaplanowane) {
            zaplanowane = true;
            window.requestAnimationFrame(update);
        }
    };

    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    update();

    const submit = bar.querySelector('[data-pdp-sticky-submit]');

    if (submit) {
        submit.addEventListener('click', () => cta.click());
    }
}

/**
 * Miekkie pojawianie sekcji przy przewijaniu.
 *
 * Bez IntersectionObservera - motyw przewija strone w kontenerze i skokowe
 * zmiany scrolla nie emituja zdarzen przeciecia (ten sam problem co w
 * initStickyBar). Klasa pdp-anim wlacza stany poczatkowe dopiero przy
 * dzialajacym JS, wiec bez skryptu tresc nigdy nie jest ukryta.
 */
function initReveal(root) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    let pozostale = [...root.querySelectorAll('.pdp-section, .pdp-story')];

    if (!pozostale.length) {
        return;
    }

    root.classList.add('pdp-anim');

    let zaplanowane = false;

    const check = () => {
        zaplanowane = false;
        const limit = window.innerHeight * 0.92;

        pozostale = pozostale.filter((section) => {
            if (section.getBoundingClientRect().top > limit) {
                return true;
            }

            section.classList.add('is-inview');
            return false;
        });
    };

    const onScroll = () => {
        if (pozostale.length && !zaplanowane) {
            zaplanowane = true;
            window.requestAnimationFrame(check);
        }
    };

    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    check();
}

/**
 * Synchronizacja belki sticky z wybranym wariantem.
 *
 * Pigułki gramatur w belce nie są osobnym formularzem — klik zaznacza
 * odpowiednie pole radio w głównym formularzu i wyzwala change, więc cała
 * logika wariantów (selecty WC, ceny, dostępność) zostaje w jednym miejscu.
 */
function initStickySync(root, form) {
    const meta = root.querySelector('[data-pdp-sticky-meta]');

    if (!meta || typeof window.jQuery === 'undefined') {
        return;
    }

    const $form = window.jQuery(form);
    const pills = [...root.querySelectorAll('[data-pdp-sticky-waga]')];
    const radioFor = (slug) => form.querySelector(`input[name="pdp_waga"][value="${slug}"]`);

    const syncPills = () => {
        pills.forEach((pill) => {
            const radio = radioFor(pill.dataset.pdpStickyWaga);
            const oos = !radio || radio.disabled;

            pill.classList.toggle(SELECTED_CLASS, !!(radio && radio.checked));
            pill.classList.toggle('is-oos', oos);

            if (oos) {
                pill.setAttribute('title', 'Brak w magazynie');
            } else {
                pill.removeAttribute('title');
            }
        });
    };

    pills.forEach((pill) => {
        pill.addEventListener('click', () => {
            const radio = radioFor(pill.dataset.pdpStickyWaga);

            if (!radio || radio.disabled || radio.checked) {
                return;
            }

            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
        });
    });

    // Stany niedostepnosci przelicza sluchacz change z initAvailability,
    // zarejestrowany PO tym module - synchronizacja pigulek po klatce,
    // zeby czytac juz zaktualizowane disabled/is-oos.
    form.addEventListener('change', () => window.requestAnimationFrame(syncPills));

    $form.on('show_variation', (event, variation) => {
        const waga = form.querySelector('input[name="pdp_waga"]:checked');
        const label = waga ? waga.closest('.pdp-weight').querySelector('.pdp-weight__label').textContent.trim() : '';
        const price = variation && variation.price_html ? variation.price_html : '';

        meta.innerHTML = label ? `${label} · ${price}` : price;
        syncPills();
    });

    $form.on('hide_variation', () => {
        meta.textContent = 'Wybierz wariant';
        syncPills();
    });

    syncPills();
}

/**
 * Belka wysylki na wysokosci przycisku "Dodaj do koszyka".
 *
 * Belka siedzi w lewej kolumnie (pod belkami zaufania), przycisk w prawej.
 * Gdy prawa kolumna jest dluzsza (duzo parametrow), przycisk laduje nizej
 * niz belka - wtedy belka zjezdza do jego wysokosci. Gdy przycisk jest
 * wyzej, belka zostaje na swoim naturalnym miejscu. Dziala tylko w ukladzie
 * dwukolumnowym (>= 992px); wysokosc prawej kolumny zmienia sie tez w
 * trakcie uzycia (rozwiniecie opcji mielenia, komunikaty wariantow),
 * stad ResizeObserver na buyboxie.
 */
function initShippingAlign(root) {
    const bar = root.querySelector('.pdp-shipping');
    const button = root.querySelector('.pdp-top .single_add_to_cart_button');

    if (!bar || !button) {
        return;
    }

    const align = () => {
        bar.style.marginTop = '';

        if (window.innerWidth < 992) {
            return;
        }

        const naturalny = parseFloat(window.getComputedStyle(bar).marginTop) || 0;
        const przesuniecie = button.getBoundingClientRect().top - bar.getBoundingClientRect().top;

        if (przesuniecie > 0) {
            bar.style.marginTop = `${Math.round(naturalny + przesuniecie)}px`;
        }
    };

    align();
    window.addEventListener('resize', align);
    window.addEventListener('load', align);

    if (typeof ResizeObserver !== 'undefined') {
        const buybox = root.querySelector('.pdp-buybox');

        if (buybox) {
            new ResizeObserver(align).observe(buybox);
        }
    }
}

export default {
    init() {
        const root = document.querySelector('.pdp');

        if (!root) {
            return;
        }

        initGallery(root);
        initBrewTabs(root);
        initReveal(root);
        initStickyBar(root);
        initQuantity(root);
        initCrossCarousel(root);
        initShippingAlign(root);

        const form = root.querySelector('.variations_form');

        if (form) {
            initAttributeRadios(form);
            initGrind(form);
            initStickySync(root, form);
            initAvailability(root, form);
            initVariationPhoto(root, form);

            // Skrypt wariantów WooCommerce inicjalizuje się niezależnie od tego
            // modułu i przy starcie przebudowuje opcje selectów, gubiąc wybór
            // ustawiony wcześniej. Sekwencja per-atrybut (change po każdym polu)
            // wpada w ping-pong z tą przebudową, więc po inicjalizacji WC
            // ustawiamy WSZYSTKIE selecty bez zdarzeń pośrednich i wyzwalamy
            // jedno check_variations.
            if (typeof window.jQuery !== 'undefined') {
                const $form = window.jQuery(form);

                const applyAll = () => {
                    form.querySelectorAll('input[type="radio"][data-pdp-attribute]:checked').forEach((radio) => {
                        // Radio w ukrytym lub zwinietym kontenerze nie
                        // reprezentuje wyboru — np. metoda mielenia zostaje
                        // zaznaczona, gdy wybrano Ziarna (panel metod jest
                        // zwiniety klasa, nie atrybutem hidden). Nalozone
                        // „po wierzchu” zerowaloby select wartością spoza
                        // listy opcji.
                        if (radio.closest('[hidden], .pdp-choice__second:not(.is-open)')) {
                            return;
                        }

                        const select = nativeSelect(form, radio.dataset.pdpAttribute);

                        if (select) {
                            select.value = radio.dataset.pdpValue || radio.value;
                        }
                    });
                    $form.trigger('check_variations');
                };

                // Drugie przejście po tyknięciu zegara: pierwsza przebudowa
                // opcji przez WC potrafi wyzerować wartości ustawione w tym
                // samym cyklu zdarzeń.
                const applyAllTwice = () => {
                    applyAll();
                    window.setTimeout(applyAll, 60);
                };

                if ($form.data('product_variations')) {
                    // WC zdążył się zainicjalizować przed nami.
                    applyAllTwice();
                } else {
                    $form.on('wc_variation_form', applyAllTwice);
                }
            }
        }
    },
};



// WEBPACK FOOTER //
// ./scripts/routes/product-v2.js